import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CryptoJS from "crypto-js";
import {
  failureMessageConstant,
  formattedDateTypes,
  keyConstants
} from "../constants";
import { jwtDecode } from "jwt-decode";
import { Auth0Service } from "../services";
const { TOKEN, IS_REFRESH_TOKEN_LOADING } = keyConstants;
const { NUMERIC, SHORT, DIGIT_TYPE } = formattedDateTypes;
export default class Utils {
  /**
   * Get item from localStorage
   * @param {string} key - The key to identify the item in localStorage
   * @returns {any|null} Parsed JSON data from localStorage, or null if the key does not exist
   */
  static localStorageGetItem = (key) => {
    const data = localStorage.getItem(key);
    if (data) return JSON.parse(data);
    return null;
  };

  /**
   * Set item in localStorage
   * @param {string} key - The key to identify the item in localStorage
   * @param {any} data - Data to be stored in localStorage (will be JSON.stringify-ed)
   */
  static localStorageSetItem = (key, data) => {
    localStorage.setItem(key, JSON.stringify(data));
  };

  /**
   * Remove item from localStorage
   * @param {string} key - The key to identify the item in localStorage
   */
  static localStorageRemoveItem = (key) => {
    localStorage.removeItem(key);
  };

  /**
   * Clears the local storage.
   *
   * @return {void}
   */
  static localStorageClear() {
    localStorage.clear();
  }

  static cookieGetItem = (key) => {
    const name = key + "=";
    const decodedCookie = decodeURIComponent(document.cookie); // Decode the cookie string
    const cookieArray = decodedCookie.split(";"); // Split into individual cookies

    for (let i = 0; i < cookieArray.length; i++) {
      let cookie = cookieArray[i].trim();
      if (cookie.indexOf(name) === 0) {
        return JSON.parse(cookie.substring(name.length, cookie.length)); // Return the parsed JSON data
      }
    }
    return null; // Return null if the key is not found
  };

  static cookieSetItem = (key, data, days = 30) => {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000); // Set expiration based on the number of days
    const expires = "expires=" + date.toUTCString();
    document.cookie =
      key +
      "=" +
      encodeURIComponent(JSON.stringify(data)) +
      ";" +
      expires +
      ";path=/";
  };

  static cookieClearItem = (key) => {
    document.cookie = key + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  };

  /**
   * This function is made to handle success and error callback!
   * @param {Promise} promise - The promise to be handled
   * @returns {Promise<Array>} A promise that resolves to an array with either [null, data] on success or [err] on failure
   */
  static async parseResponse(promise) {
    return promise.then((data) => [null, data]).catch((err) => [err]);
  }

  /**
   * Generate a GUID (Globally Unique Identifier)
   * @returns {string} A string representing a GUID
   */
  static guid() {
    const s4 = () =>
      Math.floor(Math.random() * 65536)
        .toString(16)
        .padStart(4, "0");

    return (
      s4() +
      s4() +
      "-" +
      s4() +
      "-" +
      "4" +
      s4().substring(1) +
      "-" +
      s4() +
      "-" +
      s4() +
      s4() +
      s4()
    );
  }
  /**
   * Generates a success toast message with the given message and theme.
   *
   * @param {string} message - The message to display in the toast.
   * @param {Object} theme - The theme configuration for the toast. (optional)
   * @param {string} theme.position - The position of the toast. (optional)
   * @param {number} theme.autoClose - The duration in milliseconds for the toast to automatically close. (optional)
   * @param {boolean} theme.hideProgressBar - Whether to hide the progress bar of the toast. (optional)
   * @param {boolean} theme.closeOnClick - Whether to close the toast when it is clicked. (optional)
   * @param {boolean} theme.pauseOnHover - Whether to pause the auto-close timer when the mouse hovers over the toast. (optional)
   * @param {boolean} theme.draggable - Whether the toast can be dragged. (optional)
   * @param {string} theme.theme - The theme style for the toast. (optional)
   * @param {undefined} theme.progress - The progress value for the toast. (optional)
   * @return {void} This function does not return anything.
   */
  static successToastMessage(message) {
    toast.dismiss();
    toast.success(message, {
      position: "top-center",
      autoClose: 3000,
      toastId: "successToast"
    });
  }
  /**
   * Generates a failure toast message with the given message and theme.
   *
   * @param {string} message - The message to display in the toast.
   * @param {object} theme - The theme configuration for the toast. (optional)
   * @param {string} theme.position - The position of the toast. (optional)
   * @param {number} theme.autoClose - The duration in milliseconds before the toast automatically closes. (optional)
   * @param {boolean} theme.hideProgressBar - Determines whether to hide the progress bar. (optional)
   * @param {boolean} theme.closeOnClick - Determines whether to close the toast when clicked. (optional)
   * @param {boolean} theme.pauseOnHover - Determines whether to pause the toast on hover. (optional)
   * @param {boolean} theme.draggable - Determines whether the toast can be dragged. (optional)
   * @param {string} theme.theme - The theme style for the toast. (optional)
   * @param {undefined} theme.progress - The progress configuration for the toast. (optional)
   * @return {void} Returns nothing.
   */
  static failureToastMessage(message, displayTimeInSec = 3) {
    toast.dismiss();
    toast.error(message, {
      position: "top-center",
      autoClose: displayTimeInSec * 1000,
      toastId: "failureToast"
    });
  }

  static getHeader(token) {
    return {
      headers: {
        Authorization: `Bearer ${
          token !== undefined ? token : Utils.cookieGetItem(TOKEN)?.access_token
        }`,
        "X-Api-Key": process.env.REACT_APP_X_API_KEY
      }
    };
  }
  static dateFormat(date) {
    return new Date(date).toLocaleDateString("en-US", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  }

  static handleNumericInput(event) {
    // Allow only numeric values (0-9) and certain control keys
    const allowedKeys = [
      "Backspace",
      "ArrowLeft",
      "ArrowRight",
      "Delete",
      "Tab"
    ];
    if (!/^\d$/.test(event.key) && !allowedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

  static formatTimestamp(timestamp) {
    const date = new Date(timestamp);
    const hourMinute = new Intl.DateTimeFormat("en-US", {
      hour: DIGIT_TYPE,
      minute: DIGIT_TYPE,
      hour12: true
    }).format(date);
    const day = new Intl.DateTimeFormat("en-US", { day: NUMERIC }).format(date);
    const month = new Intl.DateTimeFormat("en-US", { month: SHORT }).format(
      date
    );
    const year = new Intl.DateTimeFormat("en-US", { year: NUMERIC }).format(
      date
    );
    return `${hourMinute}, ${day} ${month} ${year}`;
  }
  static truncateWalletAddress(address = "") {
    const start = address.slice(0, 29);
    const end = address.slice(-4);
    return start + "..." + end;
  }

  static truncateString(string = "", count = 10) {
    const start = string.slice(0, count);
    const end = string.slice(-4);
    return start + "..." + end;
  }

  static isVideoURL(url) {
    // Example: Check if the URL ends with a video extension
    return /\.(mp4|mov|avi|mkv|webm)$/i.test(url);
  }

  static async checkTokenExpireOrNot(functionCB) {
    const accessToken = Utils.cookieGetItem(TOKEN)?.access_token;
    // Check if the access token exists
    if (accessToken) {
      const decodedToken = jwtDecode(accessToken);
      const currentTime = Math.floor(Date.now() / 1000);
      if (decodedToken.exp < currentTime) {
        try {
          if (Utils.localStorageGetItem(IS_REFRESH_TOKEN_LOADING)) {
            // functionCallBack is required only for GET APIs - don't pass parameter in functionCB.
            if (functionCB) setTimeout(functionCB, 500);
            return IS_REFRESH_TOKEN_LOADING;
          }
          const auth0Service = new Auth0Service();
          Utils.localStorageSetItem(IS_REFRESH_TOKEN_LOADING, true);
          const newToken = await auth0Service.refreshAccessToken();
          Utils.localStorageSetItem(IS_REFRESH_TOKEN_LOADING, false);
          if (newToken) {
            Utils.cookieSetItem(TOKEN, newToken);
            return false;
          }
        } catch (error) {
          console.error(error);
          Utils.failureToastMessage(
            failureMessageConstant.SESSION_EXPIRED_LOGIN_AGAIN
          );
          return true;
        }
      }
      return false;
    }
    Utils.failureToastMessage(
      failureMessageConstant.SESSION_EXPIRED_LOGIN_AGAIN
    );
    return true;
  }

  static formatCounter = (count) => {
    return count < 10 ? `0${count}` : count;
  };

  // Encryption function
  static encryptPassword = (password) => {
    const encrypted = CryptoJS.AES.encrypt(
      password, // actual password
      process.env.REACT_APP_SECRET_KEY // secret key
    ).toString();
    return encrypted;
  };

  // Decryption function
  static decryptPassword = (encryptedPassword) => {
    const decrypted = CryptoJS.AES.decrypt(
      encryptedPassword, // encrypted password
      process.env.REACT_APP_SECRET_KEY // secret key
    ).toString(CryptoJS.enc.Utf8);
    return decrypted;
  };
  static capitalizeFirstLetter(string = "") {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  }

  static isPurelyNumeric(value) {
    if (!value) return false;
    // Remove extra spaces and check if the string contains only numeric characters
    return /^\d+$/.test(String(value).trim());
  }

  static formatDiscounts = (discountsArray) => {
    let greatestDiscountValue = -Infinity;
    let stringDiscounts = [];

    discountsArray.forEach((discount) => {
      // Skip empty strings
      if (discount === "") return;

      if (typeof discount === "number") {
        // Directly handle numeric discounts
        greatestDiscountValue = Math.max(greatestDiscountValue, discount);
      } else if (
        typeof discount === "string" &&
        Utils.isPurelyNumeric(discount)
      ) {
        // Handle numeric strings
        const numericDiscount = parseFloat(discount);
        greatestDiscountValue = Math.max(
          greatestDiscountValue,
          numericDiscount
        );
      } else if (typeof discount === "string") {
        // Collect non-numeric string discounts
        stringDiscounts.push(discount);
      }
    });

    // Format the result
    let result = "";
    if (greatestDiscountValue !== -Infinity && greatestDiscountValue > 0) {
      result = `UP TO ${greatestDiscountValue}% OFF`;
    }

    if (stringDiscounts.length > 0) {
      if (result) {
        result += `, `;
      }
      result += stringDiscounts.join(", ");
    }

    return result;
  };

  // Function to get the discount value of the highest tier
  static getHighestTierDiscount = (consentListData) => {
    const tierHierarchy = ["Silver Tier", "Gold Tier", "Platinum Tier"];
    if (consentListData?.length > 0) {
      // Find the highest tier in the consent list
      const highestTierData = consentListData.reduce((highest, current) => {
        return tierHierarchy.indexOf(current.name) >
          tierHierarchy.indexOf(highest.name)
          ? current
          : highest;
      });

      // Return the discount value of the highest tier
      return highestTierData.discount;
    }
    return null; // No data available
  };
}
